import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Power, RefreshCw, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionLabel, StatusBadge } from "@/components/bits";
import AdminProducts from "@/components/admin/AdminProducts";
import AdminTransactions from "@/components/admin/AdminTransactions";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminCategories from "@/components/admin/AdminCategories";

const TABS = ["overview", "products", "categories", "transactions", "codes", "settings"];

export default function Admin() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", owner_name: "", owner_email: "", discount_pct: 5, commission_pct: 10 });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => {
    if (user && user.role === "admin") {
      load();
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/codes", {
        ...form,
        discount_pct: Number(form.discount_pct),
        commission_pct: Number(form.commission_pct),
      });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ code: "", owner_name: "", owner_email: "", discount_pct: 5, commission_pct: 10 });
      load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to create code");
    }
    setLoading(false);
  };

  const toggle = async (code) => {
    try { await api.post(`/admin/codes/${code}/toggle`); load(); } catch { toast.error("Failed"); }
  };

  const startEdit = (c) => {
    setEditing(c.code);
    setEditForm({
      owner_name: c.owner_name, owner_email: c.owner_email,
      discount_pct: c.discount_pct, commission_pct: c.commission_pct,
      uses: c.uses, total_sales: c.total_sales, commission_earned: c.commission_earned,
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/codes/${editing}`, {
        ...editForm,
        discount_pct: Number(editForm.discount_pct),
        commission_pct: Number(editForm.commission_pct),
        uses: Number(editForm.uses),
        total_sales: Number(editForm.total_sales),
        commission_earned: Number(editForm.commission_earned),
      });
      toast.success(`Code ${editing} updated`);
      setEditing(null);
      load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to update code");
    }
    setSaving(false);
  };

  const setEdit = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const syncVouches = async () => {
    try {
      const { data } = await api.post("/admin/vouches/refresh");
      toast.success(`Synced ${data.synced} vouches from ${data.scanned} Discord messages${data.hidden ? ` · ${data.hidden} hidden by Discord permissions` : ""}`);
    } catch {
      toast.error("Vouch sync failed");
    }
  };

  return (
    <div className="pt-32 pb-28 mx-auto max-w-[1200px] px-5 sm:px-8" data-testid="admin-page">
      <SectionLabel>ADMIN CONTROL</SectionLabel>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none">Dashboard</h1>
        <button onClick={syncVouches} data-testid="sync-vouches-btn" className="btn-ghost px-6 py-3 text-xs inline-flex items-center gap-2">
          <RefreshCw size={14} /> SYNC VOUCHES
        </button>
      </div>

      <div className="mt-8 flex flex-nowrap sm:flex-wrap gap-2 border-b border-white/10 pb-5 overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`shrink-0 font-mono text-xs tracking-[0.2em] px-4 sm:px-5 py-3 border transition-colors ${tab === t ? "bg-white text-black border-white" : "border-white/15 text-white/55 hover:border-white/50"}`}>
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "overview" && (
          <div data-testid="admin-overview">
            <div className="grid gap-px rounded-2xl overflow-hidden bg-white/10 border border-white/10 sm:grid-cols-3 lg:grid-cols-6">
              {[
                { l: "REVENUE", v: stats ? `$${stats.revenue.toFixed(2)}` : "—" },
                { l: "PAID ORDERS", v: stats ? stats.paid_orders : "—" },
                { l: "PENDING", v: stats ? stats.pending : "—" },
                { l: "USERS", v: stats ? stats.users : "—" },
                { l: "LOW STOCK", v: stats ? stats.low_stock : "—" },
                { l: "GATEWAY", v: stats ? stats.payment_provider.toUpperCase() : "—" },
              ].map((s) => (
                <div key={s.l} className="bg-black p-6">
                  <div className="font-display text-3xl leading-none">{s.v}</div>
                  <div className="mt-2 font-mono text-[9px] tracking-[0.2em] text-white/40">{s.l}</div>
                </div>
              ))}
            </div>
            <p className="mt-6 font-mono text-xs tracking-[0.15em] text-white/40">
              // MANAGE PRODUCTS, STOCK & PRICES IN THE PRODUCTS TAB · VIEW SALES IN TRANSACTIONS
            </p>
          </div>
        )}

        {tab === "products" && <AdminProducts />}
        {tab === "categories" && <AdminCategories />}
        {tab === "transactions" && <AdminTransactions />}
        {tab === "settings" && <AdminSettings />}

        {tab === "codes" && (
          <div>
            <p className="mb-8 max-w-xl text-white/50">Issue codes to staff. Buyers get a discount at checkout; staff earn tracked commission.</p>
            <form onSubmit={create} className="rounded-2xl border border-white/15 p-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-5" data-testid="create-code-form">
              {[
                { k: "code", l: "CODE", ph: "STAFF10" },
                { k: "owner_name", l: "OWNER NAME", ph: "Jay" },
                { k: "owner_email", l: "OWNER EMAIL", ph: "jay@injectd.gg", type: "email" },
                { k: "discount_pct", l: "DISCOUNT %", type: "number" },
                { k: "commission_pct", l: "COMMISSION %", type: "number" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-white/40">{f.l}</label>
                  <input
                    type={f.type || "text"} required value={form[f.k]} placeholder={f.ph} onChange={set(f.k)}
                    data-testid={`code-field-${f.k}`}
                    className="mt-2 w-full bg-transparent border border-white/15 px-3 py-3 text-sm font-mono focus:border-white focus:outline-none"
                  />
                </div>
              ))}
              <div className="sm:col-span-2 lg:col-span-5">
                <button type="submit" disabled={loading} data-testid="create-code-submit" className="btn-inj px-8 py-4 text-sm inline-flex items-center gap-2 disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} CREATE CODE
                </button>
              </div>
            </form>

            <div className="mt-10 rounded-2xl border border-white/10 overflow-x-auto" data-testid="admin-codes-list">
              <div className="grid grid-cols-[1fr_1fr_repeat(4,auto)_auto_auto] gap-4 px-6 py-4 border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-white/40 items-center min-w-[680px]">
                <span>CODE</span><span>OWNER</span><span>USES</span><span>SALES</span><span>EARNED</span><span>STATUS</span><span></span>
              </div>
              {codes.map((c) => (
                <div key={c.code}>
                  <div className="grid grid-cols-[1fr_1fr_repeat(4,auto)_auto_auto] gap-4 items-center px-6 py-4 border-b border-white/[0.06] min-w-[680px]">
                    <span className="font-mono text-sm tracking-[0.15em]">{c.code}</span>
                    <span className="text-sm text-white/60 truncate">{c.owner_name}<br /><span className="font-mono text-[10px] text-white/30">{c.owner_email}</span></span>
                    <span className="font-display text-lg">{c.uses}</span>
                    <span className="font-display text-lg">${c.total_sales.toFixed(0)}</span>
                    <span className="font-display text-lg">${c.commission_earned.toFixed(2)}</span>
                    <StatusBadge status={c.active ? "undetected" : "down"} small />
                    <button onClick={() => (editing === c.code ? setEditing(null) : startEdit(c))} data-testid={`edit-code-${c.code}`} className="text-white/50 hover:text-white transition-colors" title="Edit code">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => toggle(c.code)} data-testid={`toggle-code-${c.code}`} className="text-white/50 hover:text-white transition-colors" title="Toggle active">
                      <Power size={16} />
                    </button>
                  </div>
                  {editing === c.code && (
                    <div className="px-6 py-6 border-b border-white/[0.06] bg-white/[0.03]" data-testid={`edit-form-${c.code}`}>
                      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                          { k: "owner_name", l: "OWNER NAME" },
                          { k: "owner_email", l: "OWNER EMAIL", type: "email" },
                          { k: "discount_pct", l: "DISCOUNT %", type: "number" },
                          { k: "commission_pct", l: "COMMISSION %", type: "number" },
                          { k: "uses", l: "USES", type: "number" },
                          { k: "total_sales", l: "TOTAL SALES $", type: "number" },
                          { k: "commission_earned", l: "COMMISSION EARNED $", type: "number" },
                        ].map((f) => (
                          <div key={f.k}>
                            <label className="font-mono text-[10px] tracking-[0.2em] text-white/40">{f.l}</label>
                            <input
                              type={f.type || "text"} value={editForm[f.k]} onChange={setEdit(f.k)}
                              data-testid={`edit-field-${f.k}`}
                              className="mt-2 w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm font-mono focus:border-white focus:outline-none"
                            />
                          </div>
                        ))}
                        <div className="flex items-end gap-3">
                          <button onClick={saveEdit} disabled={saving} data-testid="save-code-edit" className="btn-inj px-6 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : null} SAVE
                          </button>
                          <button onClick={() => setEditing(null)} data-testid="cancel-code-edit" className="btn-ghost px-4 py-2.5 text-xs inline-flex items-center gap-1">
                            <X size={14} /> CANCEL
                          </button>
                        </div>
                      </div>
                      <p className="mt-4 font-mono text-[10px] tracking-[0.15em] text-white/30">// SET USES / SALES / EARNED TO REFLECT EXISTING STATS — FUTURE SALES KEEP ACCRUING ON TOP</p>
                    </div>
                  )}
                </div>
              ))}
              {codes.length === 0 && <div className="px-6 py-12 text-center font-mono text-xs tracking-[0.2em] text-white/40">// NO CODES YET</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
